import {
  serializeChargeFilters,
  serializeQuoteFilters,
} from "@/app/[tenant]/(console)/billing/table-filter-contracts";

import { getTimezone } from "@/lib/timezone";

import {
  AppointmentServiceUpdatePreview,
  ChargeDetail,
  ChargeDetailsUpdate,
  ChargeFetchParams,
  ChargeTableItem,
  ChargeTimelineItem,
  PublicCharge,
  TodaysItemEntry,
} from "@/types/charge";
import { ChargeStatus } from "@/types/enums";
import type { NoteGroupItem } from "@/types/notes";
import { Page } from "@/types/pagination";
import { RefundCreate, RefundSummary } from "@/types/refund";
import { ReversalOptions, ReversalPreview } from "@/types/reversal";

import { refundPayment } from "./payment";
import { requestData } from "@/api/api-config";

export async function fetchChargeTable(
  params: ChargeFetchParams,
  controller: AbortController | null = null,
): Promise<Page<ChargeTableItem> | null> {
  const tz = getTimezone();
  return await requestData<Page<ChargeTableItem>>(
    "billing/charge/table",
    "get",
    {
      searchParams: serializeChargeFilters({ ...params, tz }),
      signal: controller?.signal,
    },
  );
}

/** The Quotes table: draft charges, filterable by the status of their quote. */
export async function fetchQuoteTable(
  params: ChargeFetchParams,
): Promise<Page<ChargeTableItem> | null> {
  return await requestData<Page<ChargeTableItem>>(
    "billing/charge/table",
    "get",
    { searchParams: serializeQuoteFilters({ ...params, tz: getTimezone() }) },
  );
}

export async function fetchQuoteStatusGroups(
  params: ChargeFetchParams,
): Promise<NoteGroupItem[] | null> {
  return await requestData<NoteGroupItem[]>(
    "billing/charge/table/quote-status-groups",
    "get",
    { searchParams: serializeQuoteFilters({ ...params, tz: getTimezone() }) },
  );
}

export async function fetchChargeTimeline(
  chargeId?: string,
  paymentId?: string,
): Promise<ChargeTimelineItem[] | null> {
  const searchParams: Record<string, string> = {};
  if (chargeId) searchParams.charge_id = chargeId;
  if (paymentId) searchParams.payment_id = paymentId;

  return await requestData<ChargeTimelineItem[]>(
    "billing/charge/timeline",
    "get",
    {
      searchParams,
    },
  );
}

export async function listCharges(
  patientId: string,
  status: ChargeStatus,
): Promise<ChargeDetail[] | null> {
  return await requestData<ChargeDetail[]>("billing/charge/list", "get", {
    searchParams: {
      patientId,
      status,
    },
  });
}

export async function getCharge(
  chargeId: string,
): Promise<ChargeDetail | null> {
  return await requestData<ChargeDetail>(`billing/charge/get/${chargeId}`);
}

export async function getAppointmentServiceUpdatePreview(
  chargeId: string,
): Promise<AppointmentServiceUpdatePreview | null> {
  return await requestData<AppointmentServiceUpdatePreview>(
    `billing/charge/${chargeId}/appointment-service-sync-preview`,
  );
}

// Throws on failure (catchError=false) so the prompt reports an appointment
// that was not updated instead of toasting success.
export async function applyAppointmentServiceUpdate(
  chargeId: string,
  removeEventSegmentIds: string[] = [],
): Promise<AppointmentServiceUpdatePreview | null> {
  return await requestData<AppointmentServiceUpdatePreview>(
    `billing/charge/${chargeId}/appointment-service-sync`,
    "post",
    { json: { removeEventSegmentIds } },
    false,
  );
}

// Create or replace a DRAFT charge — a quote being assembled in checkout or
// in the legacy v1 cart create page. The server stores the full payload for
// exact resume and skips all payment-time side effects (no discount
// consumption, no sold packages, no inventory drawdown, no alerts).
// Throws on failure (catchError=false) so callers can show the server's
// reason — "this quote belongs to a different patient" is actionable, a
// swallowed null is not.
export async function saveDraftCharge(
  charge: ChargeDetail,
): Promise<{ id: string } | null> {
  return await requestData<{ id: string }>(
    "billing/charge/draft",
    "post",
    { json: charge },
    false,
  );
}

// Fetch a draft charge for resuming in checkout. Returns the exact payload
// the checkout saved (with fresh patient data).
export async function getDraftCharge(
  chargeId: string,
): Promise<ChargeDetail | null> {
  return await requestData<ChargeDetail>(`billing/charge/draft/${chargeId}`);
}

// Soft-delete a DRAFT charge (patient-facing quote). Uses the dedicated
// /charge/draft/{id} endpoint gated on the quotes module + billing:read,
// not the general /charge/{id} DELETE that requires payment-delete perm.
export async function deleteDraftCharge(
  chargeId: string,
): Promise<boolean | null> {
  return await requestData<boolean>(
    `billing/charge/draft/${chargeId}`,
    "delete",
    {},
    false,
  );
}

export async function fetchPublicCharge(
  chargeId: string,
): Promise<PublicCharge | null> {
  return await requestData<PublicCharge>(`billing/charge/public/${chargeId}`);
}

export async function updateChargeComment(
  chargeId: string,
  comment: string,
): Promise<boolean | null> {
  return await requestData<boolean>(
    `billing/charge/update-comment/${chargeId}`,
    "post",
    {
      json: { comment },
    },
  );
}

export async function updateChargeDetails(
  chargeId: string,
  details: ChargeDetailsUpdate,
): Promise<boolean | null> {
  return await requestData<boolean>(
    `billing/charge/update-details/${chargeId}`,
    "post",
    {
      json: details,
    },
  );
}

export async function refundChargePayment(
  refund: RefundCreate,
): Promise<RefundSummary | null> {
  return await refundPayment(refund);
}

export async function deleteCharge(
  chargeId: string,
  reversalOptions?: ReversalOptions | null,
): Promise<boolean | null> {
  return await requestData<boolean>(
    `billing/charge/${chargeId}`,
    "delete",
    reversalOptions ? { json: reversalOptions } : {},
    false,
  );
}

export async function fetchChargeReversalPreview(
  chargeId: string,
): Promise<ReversalPreview | null> {
  return await requestData<ReversalPreview>(
    `billing/charge/${chargeId}/reversal-preview`,
  );
}

export async function updateSoldByProvider(
  itemId: string,
  providerId: string | null,
  itemType: "item" | "package",
): Promise<{ chargeId: string } | null> {
  return await requestData<{ chargeId: string }>(
    "billing/charge/update-sold-by-provider",
    "post",
    {
      searchParams: {
        itemId,
        providerId,
        itemType,
      },
    },
  );
}

export async function getTodaysItems(
  patientId: string,
  // Charges the caller already has loaded in the cart. The server skips them
  // when working out what's already billed, so lines on the invoice being
  // paid aren't subtracted twice.
  excludeChargeIds: string[] = [],
): Promise<TodaysItemEntry[] | null> {
  const tz = getTimezone();
  return await requestData<TodaysItemEntry[]>(
    `billing/charge/todays-items/${patientId}`,
    "get",
    {
      searchParams: { tz, excludeChargeIds: excludeChargeIds.join(",") },
    },
  );
}

/**
 * Check if an external ID is already in use by another charge.
 * Returns true if the external ID exists, false otherwise.
 */
export async function checkExternalIdExists(
  externalId: string,
  excludeChargeId?: string,
  controller?: AbortController | null,
): Promise<boolean> {
  const result = await fetchChargeTable(
    { chargeExternalId: externalId },
    controller,
  );
  if (!result?.data?.length) return false;
  // Exclude current charge from duplicate check
  return result.data.some((charge) => charge.id !== excludeChargeId);
}
